// ─────────────────────────────────────────────
//  buildBoardWrites — pure transform from a resolved import draft into the
//  Firestore writes the daily board needs. Mirrors DailyAssignmentPage.onApplyRows
//  so the scheduled server import and the manual AIC import produce the SAME data.
//
//  Input is the FINAL draft (after staff auto-create + re-resolve), the
//  unassigned bench, and the parsed MD call list. Output:
//    { assignments: [assignmentData...], roster: { supervisors, crnas, aicId,
//      aicName, mdCallList } }
//  The caller stamps facilityId/date + status/timestamps on each assignment and
//  writes the roster onto daily_boards (merge).
//
//  Pure — no Firestore, no React.
// ─────────────────────────────────────────────
import { surgeonFields } from "./resolveSurgeon";
import { secondProviderFields, SECOND_PROVIDER_ROLES } from "../../domain/secondProvider";
import { isOnCallShift } from "../../domain/timeline";


// One draft row → an assignment doc body (facilityId/date added by the writer).
//
// `surgeons` = the facility's configured list (procedure_refs.surgeons). With
// it, a cell like "Ahmadian + 1" or "TANDON" is linked to the configured
// spelling and its annotation is preserved separately; WITHOUT it the cell is
// written through untouched, so a caller that has not threaded the list yet
// behaves exactly as before.
export function assignmentFromDraft(row, surgeons = []) {
  // ⚠️ "NO CASE TIME" AND "ON CALL" ARE TWO DIFFERENT FACTS, and this read them
  // as one. `row.onCall` is the parser's flag for a named CRNA whose sheet row
  // printed no start or end (parsePrelimAssignments) — which is most of what it
  // says: there is no case window to draw. It is NOT evidence of a call shift,
  // and writing `isOnCall` from it made every timeless row a standing 24h watch.
  //
  // Live, 2026-09-13, four rows across three facilities carried `isOnCall` and
  // only ONE was a call shift — James Slone, code "24", WC OB. The other three
  // were a "9", a "3" and a "7a-7p", each given a synthetic 07:00 → 07:00
  // window and read by the board as an all-day watch. Owner, seeing a "9"
  // offered as "Titilope Somade · in WOR 1": "titi somade is a 9 … the one on
  // call is jim slone."
  //
  // The shift CODE is what says on call, and `isOnCallShift` is the rule the
  // roster and the TV panels already use — "24", or an am/pm range with
  // identical ends ("7a-7a"). One definition, so the sheet and the roster
  // cannot disagree about who is holding the watch.
  const noCaseTime = Boolean(row.onCall);
  const onCall = noCaseTime && isOnCallShift(row.ahpShiftCode);
  return {
    orRoom: row.orRoom || "",
    ...surgeonFields(row.surgeon || "", surgeons),
    procedure: row.procedure || "",
    specialty: row.specialty || "",
    // ⚠️ KEYED ON THE MISSING TIME, NOT ON THE CALL FLAG. The 07:00 → 07:00
    // window means "this row has no case window" — `isCaselessWindow` is what
    // reads it, and it is what releases the person to the bench instead of
    // pinning them to a room they have no cases in. Re-keying it on `onCall`
    // when that flag narrowed would have left a timeless row with NO times at
    // all, and `rowClaimsAt` fails closed on a missing end — so a "9" with no
    // cases would have gone from free on the bench to busy in that room all day.
    startTime: row.startTime || (noCaseTime ? "07:00" : ""),
    endTime: row.endTime || (noCaseTime ? "07:00" : ""),
    attendingId: row.attendingId || null,
    attendingName: row.attendingName || "",
    ahpId: row.ahpId || null,
    ahpName: row.ahpName || "",
    ahpShiftCode: row.ahpShiftCode ?? null,
    ahpDepartTime: row.ahpDepartTime || "",
    // ⚠️ THE SECOND PERSON IN THE ROOM, written through the ONE field bag
    // (domain/secondProvider.secondProviderFields) that the quick-actions menu
    // and the room modal also write. Four field names spelled in one place; a
    // hand-rolled copy is one typo away from a person who exists in Firestore
    // and renders on no screen.
    //
    // They do NOT hold the seat — the preceptor holds the room — but since
    // 2026-08-20 they DO count as cover (see domain/secondProvider). Their OWN
    // shift code rides along when the sheet printed one beside the second name
    // ("Josh Larson 5 Orient Sam Gongora 3"): two people in one room leave at
    // two different times, and `orienteeDepartMinutes` prints nothing at all
    // rather than borrowing the preceptor's departure.
    ...secondProviderFields({
      id: row.orienteeId,
      name: row.orienteeName,
      shiftCode: row.orienteeShiftCode,
      shiftInherited: row.orienteeShiftInherited,
      departTime: row.orienteeDepartTime,
      // Everything the SHEET names is a teaching pair; a helping CRNA is put
      // there by a person, not by the spreadsheet.
      role: row.secondProviderRole || SECOND_PROVIDER_ROLES.ORIENTING,
    }),
    crnaReliefId: null,
    crnaReliefName: "",
    mdReliefId: null,
    mdReliefName: "",
    // Auto-routing for a multi-room CRNA (linkCrnaChains): earlier case shows
    // "Next Room: X", the later card derives "Coming from: Y" from this link.
    nextRoomId: row.nextRoomId || null,
    nextRoomName: row.nextRoomName || "",
    isOnCall: onCall,
    source: "sheet-auto",
  };
}

// Build the assignments + roster patch from a resolved draft.
//   draftRows   — final kept rows (orRoom, attendingId, ahpId, callOrder, isAIC, …)
//   unassigned  — bench CRNAs [{ name, ahpId, shiftNumber, shiftCode }]
//   mdCallList  — parsed attending call list (codes/slots/wc) for the MD Call panel
export function buildBoardWrites(draftRows, unassigned = [], mdCallList = [], notesLocums = [], surgeons = [], facilityId = null) {
  const supervisors = [];
  const crnas = [];
  let aicId = null;
  let aicName = "";

  const upsertSup = (id, name, patch) => {
    if (!id) return;
    const i = supervisors.findIndex((m) => m.id === id);
    if (i >= 0) supervisors[i] = { ...supervisors[i], ...patch };
    else supervisors.push({ id, name: name || "", ...patch });
  };
  const upsertCrna = (id, name, patch) => {
    if (!id) return;
    const i = crnas.findIndex((m) => m.id === id);
    if (i >= 0) crnas[i] = { ...crnas[i], ...patch };
    else crnas.push({ id, name: name || "", subRole: patch.subRole || "crna", ...patch });
  };

  // Everyone who HOLDS a seat somewhere today. Used at the end to make sure a
  // person who is an orientee in one room and the seat in another is never left
  // flagged as non-cover — the flag describes a role in a room, not a person.
  const seatIds = new Set();

  const assignments = [];
  for (const row of draftRows || []) {
    assignments.push(assignmentFromDraft(row, surgeons));
    if (row.attendingId) {
      upsertSup(row.attendingId, row.attendingName, row.callOrder != null ? { callOrder: row.callOrder } : {});
    }
    if (row.ahpId) {
      seatIds.add(row.ahpId);
      upsertCrna(row.ahpId, row.ahpName, row.ahpShiftCode != null ? { shiftCode: row.ahpShiftCode } : {});
    }
    // ⚠️ THE ORIENTEE IS A MEMBER OF THE DAY, NOT A STRING ON A ROW. Before
    // this they existed only as `orienteeName` on one assignment: the AIC could
    // not move them, the break board could not list them (it maps over
    // roster.crnas), and messaging did not know they were in the building.
    // "Both users need to be in today's roster, so we can move them around"
    // (product owner, 2026-08-18).
    //
    // ⚠️ `orienting` IS NOW A LABEL, NOT A VETO. It used to keep the member out
    // of `buildStaffing`'s availability pool; the product owner reversed that on
    // 2026-08-20 (both CRNAs in a room are cover), so the pool asks
    // `countsAsCover` instead and this flag only drives the `↳` and the wording
    // on the adequacy card. `countsAsCover: true` is written EXPLICITLY, even
    // though absent already means yes, because this is the one line a future
    // "students do not count" rule flips.
    if (row.orienteeId) {
      upsertCrna(row.orienteeId, row.orienteeName, {
        orienting: (row.secondProviderRole || SECOND_PROVIDER_ROLES.ORIENTING) === SECOND_PROVIDER_ROLES.ORIENTING,
        secondProviderRole: row.secondProviderRole || SECOND_PROVIDER_ROLES.ORIENTING,
        countsAsCover: true,
        ...(row.orienteeShiftCode != null ? { shiftCode: row.orienteeShiftCode } : {}),
      });
    }
    if (row.isAIC && row.attendingId) {
      aicId = row.attendingId;
      aicName = row.attendingName || "";
    }
  }

  // NOTES-row locum coverage (Day Hawk / Night Float): resolved doctors join
  // the roster as UNASSIGNED supervisors carrying their coverage window, so
  // the bench shows them with a leaving time and the staffing adequacy math
  // counts them only for the boundaries inside coverStart→coverEnd.
  for (const l of notesLocums || []) {
    if (!l || !l.id) continue; // unresolved names never invent roster entries
    // ⚠️ A COVER SHIFT THAT BELONGS TO ONE CAMPUS GOES ONLY TO THAT CAMPUS.
    // `notesLocums` is parsed once from a tenant-wide NOTES row and handed to
    // EVERY facility's write, so without this the Tampa night float also became
    // a Wesley Chapel and a Carrollwood supervisor (2026-08-24, all three at
    // once, no room at any).
    //
    // ⚠️ FAILS CLOSED WHEN THE TARGET IS UNKNOWN. A caller that does not say
    // which facility it is writing cannot be given a campus-scoped person —
    // skipping is recoverable, writing them onto the wrong board is what this
    // guard exists to stop. An UNSCOPED locum (no facilityId) still goes
    // everywhere, which is the old behaviour and is right for a cover shift
    // nobody has assigned to a campus yet.
    if (l.facilityId && l.facilityId !== facilityId) continue;
    upsertSup(l.id, l.name, {
      notesLocum: true,
      coverKind: l.kind,
      coverStart: l.coverStart,
      coverEnd: l.coverEnd,
      coverEndNextDay: Boolean(l.coverEndNextDay),
    });
  }

  // Unassigned bench CRNAs join the roster with no room.
  for (const u of unassigned || []) {
    if (!u || !u.ahpId) continue;
    const code = u.shiftCode != null ? u.shiftCode : (u.shiftNumber != null ? String(u.shiftNumber) : null);
    upsertCrna(u.ahpId, u.name, code != null ? { shiftCode: code } : {});
  }

  // A person who holds a seat anywhere today is not a SECOND provider, whatever
  // they are doing in somebody else's room — so the label goes. Clearing it here
  // (rather than ordering the upserts and hoping) means the two facts cannot
  // land in the wrong order. `countsAsCover` is untouched: they counted either
  // way since 2026-08-20.
  for (let i = 0; i < crnas.length; i += 1) {
    if (crnas[i].orienting && seatIds.has(crnas[i].id)) {
      const { orienting, secondProviderRole, ...rest } = crnas[i];
      crnas[i] = rest;
    }
  }

  const roster = { supervisors, crnas, mdCallList: mdCallList || [] };
  if (aicId) {
    roster.aicId = aicId;
    roster.aicName = aicName;
  }
  return { assignments, roster };
}
